<template>
  <el-drawer
    :title="$t('reservationOrderDetail.title')"
    :visible.sync="show"
    :append-to-body="true"
    destroy-on-close
    size="600px"
    class="reservation-order-detail"
    @close="onClose"
  >
    <div v-loading="loading.orderInfo" style="padding: 20px">
      <!-- 預約訂單 -->
      <div data-testid="order-info-section">
        <p class="section-block-title">{{ blockIndex.orderInfo }}. {{ $t('reservationOrderDetail.orderInfo.title') }}</p>
        <OrderInfoBlock
          :order="reservation"
          :loading="loading.orderInfo"
          :readonly="readonly"
          @refresh="refreshOrder"
        />
        <div class="flex justify-end">
          <BaseElButton plain @click="showHistory = true"> {{ $t('reservationOrderDetail.button.orderRecord.title') }} </BaseElButton>
          <BaseElButton v-if="!readonly && orderAgainIsShow" plain @click="orderAgain"> {{ $t('reservationOrderDetail.button.orderAgain.title') }} </BaseElButton>
          <BaseElButton
            v-if="!readonly && getData(reservation, 'AppointmentOrder.status') && checkAction('admin.appointmentOrder.updateStatus')"
            plain
            :disabled="orderStatus === 'cancel'"
            @click="onCancelOrder(reservation)"
          >
            {{ $t('reservationOrderDetail.button.cancelOrder.title') }}
          </BaseElButton>
        </div>
      </div>

      <!-- 取消資訊 -->
      <div v-if="orderStatus === 'cancel' && getData(reservation, 'AppointmentOrder.cancelReasonCode')" data-testid="cancel-info-section">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.cancelInfo }}. 取消資訊</p>
        <CancelInfoBlock :order="reservation" />
      </div>

      <!-- 車輛資訊  -->
      <div v-if="showControl.block.keeperVehicle" data-testid="keeper-vehicle-section">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.keeperVehicle }}. 車輛資訊</p>
        <KeeperVehicleInfoBlock
          :orderData="reservation"
        />
      </div>

      <!-- 預約餐廳 -->
      <template v-if="showControl.block.foodpandaRestaurant">
        <hr style="margin: 12px 0">
        <div class="order-info-section" data-testid="foodpanda-restaurant-section">
          <p class="section-block-title">{{ blockIndex.foodpandaRestaurant }}. 預約餐廳</p>
          <FoodpandaRestaurantBlock :order="reservation" />
        </div>
      </template>

      <!-- 自訂流程 -->
      <div v-if="!readonly && customFlowConfig.length" data-testid="custom-flow-section">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.customFlow }}. {{ $t('reservationOrderDetail.customFlow.title') }}</p>
        <CustomFlowArea
          :orderId="getData(reservation, 'AppointmentOrderId')"
          :customFlowConfig="customFlowConfig"
          :record="orderCustomFlowRecord"
          @refresh="refreshOrder"
        />
      </div>

      <!-- 詳細資訊 -->
      <hr style="margin: 12px 0">
      <div data-testid="detail-section">
        <p class="section-block-title">{{ blockIndex.orderDetail }}. {{ $t('reservationOrderDetail.orderDetail.title') }}</p>
        <OrderDetailBlock :order="reservation" :syncData="syncData" :readonly="readonly" @refresh="refreshOrder" />
      </div>

      <template v-if="showControl.block.signatureDetail">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.signatureDetail }}. 簽名同意書 </p>
        <FlexLayout col :gap="12">
          <div v-if="showControl.hint.signatureUserHint" class="text-sm text-gray-60">
            此為預約者簽署狀況，非此服務對象簽署狀況
          </div>
          <SignatureDetailBlock
            @goOrder="onGoOrder"
          />
        </FlexLayout>
      </template>

      <div v-if="!readonly && (showPayment || showPubAptPayment)" data-testid="order-payment-section">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.paymentInfo }}. {{ $t('reservationOrderDetail.paymentInfo.title') }}</p>

        <div class="flex flex-col" style="gap: 20px">
          <!-- 付款資訊 -->

          <!-- 定金-->
          <OrderPaymentBlock
            v-if="getData(reservation, 'AppointmentOrder.AppointmentOrderDeposit')"
            data-testid="deposit-block"
            :reservation="reservation"
            @refresh="refreshOrder"
          />

          <!-- 結帳 -->
          <div v-if="chargeType === 'checkout'" class="flex items-end justify-between" data-testid="checkout-block">
            <CheckoutBlock
              :reservation="reservation"
              @refresh="refreshOrder"
            />
            <div class="flex justify-end pt-[20px]">
              <BaseElButton plain @click="drawer.checkoutDetail = true">{{ $t('reservationOrderDetail.button.checkoutDetail.title') }}</BaseElButton>
            </div>
          </div>

          <!-- 合併付款 -->
          <div
            v-if="getData(reservation, 'AppointmentOrder.AppointmentBatchOrder')"
            class="flex items-end justify-between"
            data-testid="merge-checkout-block"
          >
            <BatchPaymentBlock
              :chargeType="chargeType"
              :reservation="reservation"
              @refresh="refreshOrder"
            />
            <div class="flex justify-end pt-[20px]">
              <BaseElButton plain @click="drawer.batchDetail = true">{{ $t('reservationOrderDetail.button.batchDetail.title') }}</BaseElButton>
            </div>
          </div>

          <!-- 堂票預約資訊 -->
          <ClassTicketDetailBlock
            v-if="useReservationClassTicket"
            data-testid="classTicket-block"
            :orderStatus="orderStatus"
            :orderId="getData(reservation, 'AppointmentOrderId')"
            :reservationId="getData(reservation, 'id')"
            :classTickets.sync="syncData.classTickets"
            :service="syncData.serviceDetail"
            @refresh="refreshOrder"
          />
          <!-- 訪客付款資訊 -->
          <OrderPubAptPaymentBlock
            v-if="showPubAptPayment"
            data-testid="pubApt-block"
            :reservation="reservation"
            @refresh="refreshOrder"
          />
        </div>
      </div>
      <div v-if="!readonly && showCheckIn" data-testid="checkin-block">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.checkInInfo }}. {{ $t("reservationOrderDetail.checkInfo.title") }}</p>
        <CheckInBlock :order="getData(reservation, 'AppointmentOrder')" />
      </div>

      <div v-if="!readonly && showInvoice" v-loading="loading.invoice" data-testid="invoice-block">
        <hr style="margin: 12px 0">
        <p class="section-block-title">{{ blockIndex.invoiceInfo }}. {{ $t('reservationOrderDetail.invoiceInfo.title') }}</p>
        <InvoiceBlock
          :order="getData(reservation, 'AppointmentOrder')"
          @updateInvoiceStatus="updateInvoiceStatus"
        />
      </div>
    </div>

    <div class="dialog-footer flex justify-end" style="padding: 20px">
      <BaseElButton plain @click="$emit('close')">{{ $t('common.button.return.text') }}</BaseElButton>
    </div>

    <ReservationOrderHistory
      :show.sync="showHistory"
      :orderId="getData(reservation, 'AppointmentOrder.id')"
      :reservationId="getData(reservation, 'id')"
      @close="showHistory = false"
    />

    <OrderCheckoutReceiptDrawer
      v-if="drawer.checkoutDetail"
      :orderCheckoutData="orderCheckoutData"
      @close="drawer.checkoutDetail = false"
    />

    <BatchOrderPaymentDrawer
      v-if="drawer.batchDetail"
      :chargeType="chargeType"
      :batchOrder="batchOrder"
      @close="drawer.batchDetail = false"
    />

    <!--
      取消原因彈窗必須放在抽屜內部，與 EditReservationStatusDialog 同層。
      此抽屜帶 append-to-body，Element 會把根節點搬到 document.body，若彈窗
      放在父層則與抽屜互為兄弟，父層 children list 的 patch 會以已搬走的節點
      當插入參考而拋 insertBefore NotFoundError [CNCY-10342]
    -->
    <CancelReasonDialog
      v-if="cancelReasonDialog"
      @close="cancelReasonDialog = false"
      @confirm="onCancelReasonConfirm"
    />
  </el-drawer>
</template>

<script>
import {
  computed,
  defineComponent,
  reactive,
  ref,
  getCurrentInstance,
  onMounted,
  provide,
} from 'vue'
import { statusTranslate } from '@/utils/helper'
import { FindReservation, UpdateReservationCheckoutInvoiceStatus } from '@/api/reservation'
import { UpdatePubAptInvoiceStatus } from '@/api/pubApt'
import { FindService } from '@/api/service'
import { useCreateAppointment } from '@/use/useCreateAppointment'
import dayjs from '@/lib/dayjs'
import { get, isEqual } from 'lodash'
import { usePermissions } from '@/use/permissions'
import { useFetch } from '@/use/fetch'
import { useCustomFlow } from '@/use/useCustomFlow'
import { useShop } from '@/use/shop'
import OrderInfoBlock from './components/OrderInfoBlock.vue'
import OrderDetailBlock from './components/OrderDetailBlock.vue'
import OrderPubAptPaymentBlock from './components/OrderPubAptPaymentBlock.vue'
import OrderPaymentBlock from './components/OrderPaymentBlock.vue'
import ClassTicketDetailBlock from './components/ClassTicketDetailBlock.vue'
import CheckInBlock from './components/CheckInBlock.vue'
import InvoiceBlock from './components/InvoiceBlock.vue'
import { getData } from '@/utils/object'
import ReservationOrderHistory from '@/components/Reservation/ReservationOrderHistory/ReservationOrderHistory.vue'
import CustomFlowArea from '@/components/CustomFlowArea.vue'
import CheckoutBlock from './components/CheckoutBlock.vue'
import OrderCheckoutReceiptDrawer from './components/OrderCheckoutReceiptDrawer.vue'
import { useRouter } from 'vue-router/composables'
import BatchPaymentBlock from './components/BatchPaymentBlock.vue'
import BatchOrderPaymentDrawer from './components/BatchOrderPaymentDrawer.vue'
import { i18n } from '@/plugins/i18n/i18n'
import KeeperVehicleInfoBlock from './components/KeeperVehicleInfoBlock.vue'
import CancelInfoBlock from './components/CancelInfoBlock.vue'
import SignatureDetailBlock from './components/SignatureDetailBlock.vue'
import FoodpandaRestaurantBlock from './FoodpandaRestaurantBlock.vue'
import CancelReasonDialog from '@/components/Reservation/CancelReasonDialog/CancelReasonDialog.vue'
import { serviceTypeConfig } from '@/config/reservation'

const featureKeys = {
  useClassTicket: 'admin.appointmentReservationTicket.find',
  useCheckIn: 'admin.checkInPageSetting.page',
  useSelfCheckIn: 'planModule.selfCheckIn.enable',
  useOrderAgain: 'admin.appointmentOrder.create',
  useFoodpandaRestaurant: 'planModule.foodpandaRestaurant.*',
}

export default defineComponent({
  name: 'ReservationOrderDetail',
  components: {
    CheckoutBlock,
    OrderInfoBlock,
    OrderDetailBlock,
    OrderPaymentBlock,
    OrderPubAptPaymentBlock,
    ReservationOrderHistory,
    ClassTicketDetailBlock,
    CheckInBlock,
    InvoiceBlock,
    CustomFlowArea,
    OrderCheckoutReceiptDrawer,
    BatchOrderPaymentDrawer,
    BatchPaymentBlock,
    KeeperVehicleInfoBlock,
    CancelInfoBlock,
    SignatureDetailBlock,
    FoodpandaRestaurantBlock,
    CancelReasonDialog,
  },
  props: {
    // show: Boolean,
    width: String,
    selecReservation: Object,
    customFlowConfig: { type: Array, default: () => [] },
    orderId: String,
    readonly: Boolean,
    // 店家是否開啟「取消原因收集」；開啟時由抽屜內部彈窗收集原因後一併 emit
    enableCancelReasonCollection: Boolean,
  },
  setup (props, { emit }) {
    const router = useRouter()
    const show = ref(false)
    const loading = reactive({
      orderInfo: false,
      invoice: false,
    })
    const drawer = reactive({
      checkoutDetail: false,
      batchDetail: false,
    })
    const refreshOrder = ref(null)
    const { checkAction } = usePermissions()
    const { simpleFetch } = useFetch()
    const { useCustomFlowFeature } = useCustomFlow()
    const { shopId, shopTimezone } = useShop()
    const { presetData } = useCreateAppointment()
    const syncData = reactive({
      classTickets: [],
      serviceDetail: {},
    })
    const orderCheckoutData = computed(() => get(reservation.value, 'AppointmentOrder.AppointmentCheckoutOrder'))
    const batchOrder = computed(() => get(reservation.value, 'AppointmentOrder.AppointmentBatchOrder'))
    const chargeType = computed(() => get(reservation.value, 'AppointmentOrder.chargeType'))
    const useVisitorPreOrder = computed(() =>
      checkAction('adminView.appointmentReservation.includeAppointmentVisitorPreOrder'),
    )
    const usePubAptPaymentConfig = computed(() => checkAction('admin.pubAptPaymentConfig.page'))
    const reservation = ref({})
    const orderCustomFlowRecord = computed(
      () => get(reservation.value, 'AppointmentOrder.CustomFlowRecords') || [],
    )
    const useReservationClassTicket = computed(() => checkAction(featureKeys.useClassTicket))
    const orderStatus = computed(() => get(reservation.value, 'status'))
    const showPubAptPayment = computed(
      () =>
        (get(
          reservation.value,
          'AppointmentOrder.AppointmentVisitorPreOrder.DepositPubAptPaymentOrder',
        ) ||
          get(
            reservation.value,
            'AppointmentOrder.AppointmentVisitorPreOrder.PenaltyPubAptPaymentOrder',
          ) ||
          get(
            reservation.value,
            'AppointmentOrder.AppointmentVisitorPreOrder.CardSecretPubAptPaymentOrder',
          )) &&
        usePubAptPaymentConfig.value,
    )
    const showPayment = computed(
      () =>
        get(reservation.value, 'AppointmentOrder.AppointmentOrderDeposit') ||
        get(reservation.value, 'isUseClassTicket') ||
        chargeType.value === 'checkout',
    )
    const showCheckIn = computed(() => checkAction(featureKeys.useCheckIn))
    const showSelfCheckIn = computed(() => checkAction(featureKeys.useSelfCheckIn))
    const showInvoice = computed(() => {
      const pubAptInvoice = get(
        reservation.value,
        'AppointmentOrder.AppointmentVisitorPreOrder.PubAptInvoiceOrder',
      )
      const checkoutInvoice = get(
        reservation.value,
        'AppointmentOrder.AppointmentCheckoutOrder.AppointmentCheckoutInvoice',
      )
      return pubAptInvoice || checkoutInvoice
    })

    const showControl = computed(() => {
      const controls = {
        block: {
          keeperVehicle: false,
          // Only show signature detail when there are signature mappings and it's not a batch (merged) order
          signatureDetail: !!get(reservation.value, 'AppointmentOrder.SignatureRecordMappings'),
          foodpandaRestaurant: false,
        },
        hint: {
          signatureUserHint: get(reservation.value, 'AppointmentOrder.peopleCount', 0) > 1,
        },
      }

      // keeper
      if (get(reservation.value, 'AppointmentOrder.customInfo.keeperVehicle')) {
        controls.block.keeperVehicle = true
      }

      // foodpandaRestaurant
      if (checkAction(featureKeys.useFoodpandaRestaurant) && get(reservation.value, 'AppointmentOrder.customInfo.foodpandaRestaurant')) {
        controls.block.foodpandaRestaurant = true
      }

      // signature: 只要有 SignatureRecordMappings 且長度 > 0 就顯示（不再檢查是否有圖片）
      const signatureMappings = get(reservation.value, 'AppointmentOrder.SignatureRecordMappings')
      if (!signatureMappings || !get(signatureMappings, 'length')) {
        controls.block.signatureDetail = false
      }

      // 如果為合併訂單 (有 AppointmentBatchOrder)，則不顯示簽署區塊
      if (get(reservation.value, 'AppointmentOrder.AppointmentBatchOrder')) {
        controls.block.signatureDetail = false
      }

      return controls
    })

    const toOrderIndex = (indexConfig) => {
      const result = {}
      let index = 0
      for (const key in indexConfig) {
        if (indexConfig[key]) {
          index += 1
          result[key] = index
        }
      }
      return result
    }

    const blockIndex = computed(() => {
      const indexConfig = {
        orderInfo: true,
        cancelInfo: !!(orderStatus.value === 'cancel' && getData(reservation.value, 'AppointmentOrder.cancelReasonCode')),
        keeperVehicle: true,
        foodpandaRestaurant: true,
        customFlow: true,
        orderDetail: true,
        signatureDetail: false,
        paymentInfo: true,
        checkInInfo: true,
        invoiceInfo: true,
      }
      if (props.readonly || !props.customFlowConfig.length) indexConfig.customFlow = false
      indexConfig.checkInInfo = !props.readonly && (showCheckIn.value || showSelfCheckIn.value)

      if (props.readonly || (!chargeType.value !== 'checkout' && !showPayment.value && !showPubAptPayment.value)) {
        indexConfig.paymentInfo = false
      }

      if (!showControl.value.block.keeperVehicle) {
        indexConfig.keeperVehicle = false
      }

      if (showControl.value.block.signatureDetail) {
        indexConfig.signatureDetail = true
      }

      if (!showControl.value.block.foodpandaRestaurant) {
        indexConfig.foodpandaRestaurant = false
      }

      return toOrderIndex(indexConfig)
    })
    const updateInvoiceStatus = async (invoiceStatus) => {
      if (loading.invoice) return
      loading.invoice = true
      let invoiceOrderId
      let apiMethod
      if (chargeType.value === 'checkout') {
        apiMethod = UpdateReservationCheckoutInvoiceStatus
        invoiceOrderId = get(reservation.value, 'AppointmentOrder.AppointmentCheckoutOrder.id')
      } else {
        apiMethod = UpdatePubAptInvoiceStatus
        invoiceOrderId = get(reservation.value, 'AppointmentOrder.AppointmentVisitorPreOrder.id')
      }

      const [, err] = await apiMethod({
        shopId: shopId.value,
        id: invoiceOrderId,
        invoiceStatus,
      })

      if (err) {
        window.$message.error(err)
        loading.invoice = false
        return
      }
      window.$message.success(i18n.t('reservationOrderDetail.message.updateInvoiceStatusSuccess'))

      await refreshOrder.value()
      loading.invoice = false
    }

    const transformReservationToPreset = (reservationData) => {
      if (!reservationData) return null
      // 多人預約
      const peopleCount = get(reservationData, 'AppointmentOrder.peopleCount', 0)

      const memberId = get(reservationData, 'MemberId')
      const serviceUnit = get(reservationData, 'AppointmentUnit')
      const resourceItem = get(reservationData, 'ResourceItem')
      const attachedServices = get(reservationData, 'AppointmentServiceAttaches', [])
      const resourceItemTotalBookingTime = get(reservationData, 'resourceItemTotalBookingTime')
      const totalBookingTime = get(reservationData, 'totalBookingTime')

      const serviceType = (() => {
        if (serviceUnit && resourceItem) return serviceTypeConfig.humanAndResourceService.value
        if (serviceUnit) return serviceTypeConfig.humanService.value
        return serviceTypeConfig.resourceService.value
      })()

      return {
        memberId,
        userType: memberId && peopleCount === 1 ? 'member' : 'guest',
        userName: get(reservationData, 'userName'),
        userPhone: get(reservationData, 'userPhone'),
        serviceUnit,
        resourceItem,
        serviceType,
        service: get(reservationData, 'AppointmentService'),
        subService: get(reservationData, 'AppointmentSubService'),
        attachedServices,
        resourceTotalBookingTime: resourceItemTotalBookingTime,
        bookingTime: totalBookingTime,
      }
    }

    const orderAgain = async () => {
      try {
        const reservationData = reservation.value
        const peopleCount = get(reservation.value, 'AppointmentOrder.peopleCount', 0)
        // 轉換並設定 presetData
        const preset = transformReservationToPreset(reservationData)
        if (preset) {
          presetData.value = preset
        }

        // 跳轉至新版預約頁面
        router.push({
          name: 'NewReservationCreate',
          query: {
            member: reservationData.MemberId && peopleCount === 1 ? reservationData.MemberId : undefined,
          },
        })
      } catch (error) {
        console.error(error)
        window.$message.error('再次預約失敗')
      }
    }
    const orderAgainIsShow = computed(() => {
      return !props.readonly && checkAction(featureKeys.useOrderAgain)
    })

    const onGoOrder = (orderCode) => {
      show.value = false
      emit('goOrder', orderCode)
    }

    onMounted(() => {
      const vm = getCurrentInstance().proxy
      refreshOrder.value = vm.refreshOrder
      show.value = true
    })

    const onClose = () => {
      show.value = false
      setTimeout(() => {
        emit('close')
      }, 150)
    }

    provide('reservation', reservation)

    return {
      useReservationClassTicket,
      syncData,
      simpleFetch,
      orderStatus,
      reservation,
      showPayment,
      showCheckIn,
      showSelfCheckIn,
      showInvoice,
      toOrderIndex,
      blockIndex,
      useCustomFlowFeature,
      useVisitorPreOrder,
      orderCustomFlowRecord,
      updateInvoiceStatus,
      showPubAptPayment,
      loading,
      chargeType,
      show,
      onClose,
      drawer,
      orderCheckoutData,
      orderAgain,
      orderAgainIsShow,
      batchOrder,
      shopTimezone,
      showControl,
      checkAction,
      onGoOrder,
    }
  },
  data: () => ({
    showHistory: false,
    cancelReasonDialog: false,
  }),
  computed: {
    shopId () {
      return this.$store.getters.shop
    },
    page () {
      return this.$route.name
    },
    attachServices () {
      if (!get(this.reservation, 'AppointmentServiceAttaches')) return [{ name: '無', id: 0 }]
      return get(this.reservation, 'AppointmentServiceAttaches')
    },
    syncShow: {
      get () {
        return this.show
      },

      set (data) {
        this.$emit('opend:show', data)
      },
    },
  },
  watch: {
    async show () {
      if (this.show) {
        this.refreshOrder({ emit: false })
      }
    },
  },

  methods: {
    getData,
    statusContent: (status) => statusTranslate(status),

    dateFormat (date) {
      if (!date) return '無'
      return dayjs(date, this.shopTimezone).format('YYYY/MM/DD HH:mm:ss')
    },

    async findReservation () {
      const res = await FindReservation({
        shopId: this.shopId,
        id: this.reservation?.id || this.orderId,
        includeCustomFlowRecord: this.useCustomFlowFeature.record,
        includeAppointmentVisitorPreOrder: this.useVisitorPreOrder,
        includeSignatureMapping: this.checkAction('admin.signatureTemplate.find'),
      })
      this.reservation = res
      if (!isEqual(get(this.reservation, 'updatedAt'), get(this.selecReservation, 'updatedAt'))) {
        this.$emit('refresh')
      }
    },

    async onCancelOrder (reservation) {
      if (this.readonly) return
      // 開啟取消原因收集時，先在抽屜內收集原因，再連同原因一起交給父層送出
      if (this.enableCancelReasonCollection) {
        this.cancelReasonDialog = true
        return
      }
      this.$emit('cancel', reservation)
      // this.refreshOrder()
    },

    onCancelReasonConfirm (cancelReason) {
      this.cancelReasonDialog = false
      this.$emit('cancel', this.reservation, cancelReason)
    },

    async refreshOrder ({ emit } = { emit: true }) {
      if (this.loading.orderInfo) return
      this.loading.orderInfo = true
      await this.findReservation()
      await this.simpleFetch(
        FindService,
        { shopId: this.shopId, id: get(this.reservation, 'AppointmentServiceId') },
        (res) => {
          this.syncData.serviceDetail = res
        },
      )
      this.loading.orderInfo = false

      if (emit) this.$emit('refresh')
    },
  },
})
</script>

<style lang="postcss" scoped>
::v-deep .el-form-item {
  @apply mb-0;
}

.section-block-title {
  @apply text-primary-100 font-medium text-[18px] pb-[12px];
}
</style>
